<template>
	<v-app style="background-color: #F4F4F2;">
		<v-app-bar app color="#F4F4F2" elevation="0" class="px-4 py-6">
			<div class="d-flex align-center" style="cursor: pointer;">
				<a href="https://www.orixe.eu/" target="_blank">
					<v-img
						class="mt-0 pt-0"
						alt="Orixe Logo"
						:src="require('../../assets/Logo_teal_stacked_0.25x-removebg-preview.png')"
						width="200"
					/>
				</a>
			</div>
		</v-app-bar>
		<v-main>
		<v-container fluid class="pa-4 pa-sm-8">
			<v-row justify="center">
				<v-col cols="12" sm="10" md="7" lg="5">
					<v-card elevation="0" class="registration-card">
						<!-- Card Header -->
						<div class="card-header pa-6">
							<v-img
								v-if="logoUrl"
								:src="logoUrl"
								max-width="100"
								class="mb-4"
							/>
							<v-chip class="reg-badge mb-3">
								<v-icon left>fas fa-user-plus</v-icon>
								New Account
							</v-chip>
							<h2 class="header-title mb-2">Create your account</h2>
							<p class="header-subtitle mb-4">
								Register to get started.
								Fill in your company and personal details below.
							</p>
							<!-- <div class="info-banner">
								<v-icon small class="info-icon">fas fa-shield-alt</v-icon>
								<p class="info-text">
									Your details are submitted directly to
									<strong>{{ clientname || 'COMPANY' }}</strong>
									and are not shared with any other party.
								</p>
							</div> -->
						</div>
						<!-- Card Form -->
						<v-form @submit.prevent="register" v-model="valid">
							<v-card-text class="pa-6">
								<!-- Company Section -->
								<p class="section-label mb-4">Company Information</p>
									<v-alert
										v-if="duplicateError"
										type="error"
										dense
										text
										class="mb-4"
									>
										{{ duplicateError }}
									</v-alert>
								<!-- Company name field with search suggestions -->
								<div style="position: relative;" ref="nameWrapper">
									<v-text-field
										v-model="name"
										label="Company name"
										name="company"
										append-icon="fas fa-building"
										type="text"
										outlined
										dense
										class="styled-field"
										placeholder="Acme Ltd."
										:rules="[v => !!v || 'This field is required.']"
										autocomplete="off"
										@input="onNameInput"
									/>
 
									<v-card
										v-if="showCompanySuggestions && filteredByCompany.length > 0"
										class="mt-1 elevation-5 rounded-lg"
										style="position: absolute; z-index: 999; width: 100%;"
									>
										<v-list dense class="py-0" style="max-height: 200px; overflow-y: auto;">
											<v-list-item
												v-for="client in filteredByCompany"
												:key="client.ClientName"
												@click.stop="selectClient(client)"
											>
												<v-list-item-avatar>
													<v-avatar color="primary" size="36">
														<span class="white--text">{{ client.ClientName.charAt(0) }}</span>
													</v-avatar>
												</v-list-item-avatar>
												<v-list-item-content>
													<v-list-item-title>{{ client.ClientName }}</v-list-item-title>
													<v-list-item-subtitle>{{ client.Email }}</v-list-item-subtitle>
												</v-list-item-content>
											</v-list-item>
										</v-list>
									</v-card>
								</div>
 
								<v-select
									v-model="selectedCountry"
									label="Country of operation"
									:items="filteredCountries"
									item-text="country"
									outlined
									dense
									class="styled-field"
									:rules="[v => !!v || 'Please select a country.']"
									menu-props="{ closeOnClick: false, closeOnContentClick: false }"
								>
									<template v-slot:prepend-item>
										<v-list-item>
											<v-list-item-content>
											<v-text-field
												v-model="searchText"
												label="Search country"
												clearable
												dense
												@click:clear="clearSearch"
												@input="filterCountries"
											/>
											</v-list-item-content>
										</v-list-item>
									</template>
								</v-select>
								<v-text-field
									v-model="companyRegistrationNumber"
									label="Company registration number"
									name="companyregistration"
									append-icon="fas fa-address-card"
									type="text"
									outlined
									dense
									class="styled-field"
									placeholder="4–25 characters"
									:rules="[
									v => !!v || 'This field is required.',
									v => (v && v.length >= 4 && v.length <= 25) || 'Must be between 4 and 25 characters.',
									]"
								/>
								<p class="hint-text mb-6">
									Not sure of the format?
									<a href="/Company_Registration_Number_Format.pdf" target="_blank" class="teal-link">
										See examples by country →
									</a>
								</p>
								<v-divider class="mb-6" />
								<!-- User Section -->
								<p class="section-label mb-4">User Information</p>
								<v-row class="mb-1">
									<v-col cols="12" sm="6" class="py-0">
										<v-text-field
											v-model="firstname"
											label="First name"
											name="firstname"
											append-icon="fas fa-user"
											type="text"
											outlined
											dense
											class="styled-field"
											:rules="[v => !!v || 'This field is required.']"
										/>
									</v-col>
									<v-col cols="12" sm="6" class="py-0">
										<v-text-field
											v-model="lastname"
											label="Last name"
											name="lastname"
											append-icon="fas fa-user"
											type="text"
											outlined
											dense
											class="styled-field"
											:rules="[v => !!v || 'This field is required.']"
										/>
									</v-col>
								</v-row>
 
								<!-- Email field with search suggestions -->
								<div style="position: relative;" ref="emailWrapper">
									<v-text-field
										id="email"
										v-model="email"
										label="Email address"
										name="email"
										append-icon="fas fa-envelope"
										type="text"
										outlined
										dense
										class="styled-field"
										placeholder="you@company.com"
										@input="onEmailInput"
										:rules="[
										v => !!v || 'This field is required.',
										v => /@/.test(v) || 'Email must contain @ symbol.',
										v => !/[\/,]/.test(v) || 'Only one email allowed — remove / or ,',
										v => /^[^@]+@[^@]+\.[a-zA-Z]{2,}$/.test(v) || 'Invalid email format (e.g. user@domain.com).',
										]"
									></v-text-field>
 
									<v-card
										v-if="showEmailSuggestions && filteredByEmail.length > 0"
										class="mt-1 elevation-5 rounded-lg"
										style="position: absolute; z-index: 999; width: 100%;"
									>
										<v-list dense class="py-0" style="max-height: 200px; overflow-y: auto;">
											<v-list-item
												v-for="client in filteredByEmail"
												:key="client.Email"
												@click.stop="selectClient(client)"
											>
												<v-list-item-avatar>
													<v-avatar color="primary" size="36">
														<span class="white--text">{{ client.ClientName.charAt(0) }}</span>
													</v-avatar>
												</v-list-item-avatar>
												<v-list-item-content>
													<v-list-item-title>{{ client.ClientName }}</v-list-item-title>
													<v-list-item-subtitle>{{ client.Email }}</v-list-item-subtitle>
												</v-list-item-content>
											</v-list-item>
										</v-list>
									</v-card>
								</div>
 
								<secure-password-field
									id="password"
									v-model="password"
									label="Password"
									name="password"
									:append-icon="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
									:type="showPassword ? 'text' : 'password'"
									outlined
									dense
									class="styled-field"
									:rejection-errors="passwordRejectionErrors"
									@click:append="showPassword = !showPassword"
									:rules="[
									v => !!v || 'This field is required.',
									v => v.length >= 8 || 'Password must be at least 8 characters.',
									]"
								/>
								<secure-password-field
									id="password2"
									v-model="password2"
									label="Confirm Password"
									name="password2"
									:append-icon="showConfirmPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
									:type="showConfirmPassword ? 'text' : 'password'"
									outlined
									dense
									class="styled-field"
									:show-guidance="false"
									:enforce-policy="false"
									@click:append="showConfirmPassword = !showConfirmPassword"
									:rules="[matchPasswordRule(password)]"
								/>
								<v-divider class="mb-6" />
 
								<h3 class="mb-2">Security confirmation</h3>
								<v-checkbox
									v-model="securityAckReuse"
									:rules="[(v) => !!v || 'Please confirm']"
									label="I will not reuse a password from another website or app"
									hide-details="auto"
								/>
								<v-checkbox
									v-model="securityAckManager"
									:rules="[(v) => !!v || 'Please confirm']"
									label="I will keep this password private (e.g. in a password manager)"
									hide-details="auto"
								/>
								<v-divider class="mb-6 mt-6" />
								<v-btn
									:disabled="!valid || isDuplicateClient"
									type="submit"
									block
									large
									depressed
									class="continue-btn"
								>
									<v-icon left small>fas fa-user-check</v-icon>
									Create Account
								</v-btn>
								<!-- <v-btn
									:disabled="!valid"
									type="button"
									block
									outlined
									large
									depressed
									class="guest-btn mt-4"
									@click="confirmGuest = true"
								>
									<v-icon left small>fas fa-user</v-icon>
									Continue without an Account
								</v-btn> -->
								<p class="login-link-text mt-4 text-center">
									Already have an account?
									<router-link to="/login" class="teal-link font-weight-medium">
									Sign in →
									</router-link>
								</p>
								<p class="login-link-text mt-4 text-center">
									Are you a guest?
									<span
										@click="valid && !isDuplicateClient && (confirmGuest = true)"
										class="teal-link font-weight-medium"
										:class="{ 'link-disabled': !valid || isDuplicateClient }"
									>
										Continue as a guest →
									</span>
								</p>
								<div class="text-center mt-3">
									<router-link class="help-link"
										:to="{
											path: '/knowledge',
											query: {
												article: 'Registration, Login, Inviting Suppliers',
												doc: 'register-login'
											}
										}"
										target="_blank"><v-icon small class="mr-1">far fa-circle-question</v-icon> Need help?
									</router-link>
								</div>
 
							</v-card-text>
						</v-form>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
		<v-dialog v-model="confirmGuest" max-width="720px">
			<v-card class="confirm-card">
				<v-btn
					icon
					class="dialog-close-btn"
					@click="confirmGuest = false"
				>
					<v-icon size="18" color="#c0392b">fas fa-times</v-icon>
				</v-btn>
				<v-card-text class="text-center px-8 pt-10 pb-8">
					<div class="dialog-icon-wrap mx-auto mb-6">
						<v-icon size="24" color="#0b7078">far fa-circle-question</v-icon>
					</div>
 
					<h2 class="dialog-title mb-6">Are you sure you want to continue without an account?</h2>
 
					<p class="dialog-subtitle mb-4">
						As a guest, you can complete and submit your audit after verifying your email. <strong>But you won't be able to:</strong>
					</p>
 
					<ul class="guest-limits mb-6">
						<li><v-icon x-small color="#c0392b" class="mr-2">fas fa-times</v-icon><span>Invite colleagues to collaborate</span></li>
						<li><v-icon x-small color="#c0392b" class="mr-2">fas fa-times</v-icon><span>Access your submission after it's sent</span></li>
						<li><v-icon x-small color="#c0392b" class="mr-2">fas fa-times</v-icon><span>Use Flow-Down Audit, Onsite Audit, Trace, CO2, or Sustainability modules</span></li>
					</ul>
 
					<p class="recommend-text mb-6">We recommend registering to keep full access.</p>
 
					<div class="d-flex justify-center">
						<v-btn
							depressed
							dark
							color="#0b7078"
							class="px-8 mr-3 register-btn"
							@click="confirmGuest = false"
						>
							Continue to Register
						</v-btn>
						<v-btn
							text
							color="#999"
							class="px-4 guest-link-btn"
							@click="continueAsGuest"
						>
							Continue as Guest Anyway
						</v-btn>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
		</v-main>
 
		<test-error />
		<orixe-footer/>
	</v-app>
</template>
 
<script>
import router from "../../router"
import InvitationcodeAPI from "../../api/connections/invitationcodes"
import ConnectionAPI from "../../api/connections/connection"
import LoadingState from "../../consts/LoadingState"
import { mapState, mapActions } from "vuex"
import SecurePasswordField from "../../components/SecurePasswordField.vue"
import { matchPasswordRule } from "../../utils/passwordPolicy"
import { extractApiErrorDetails } from "../../utils/recaptcha"
export default {
	name: "Register",
	components: { SecurePasswordField },
 
	data() {
		return {
			logoUrl: null,
			clientname: null,
			name: null,
			password: null,
			password2: null,
			email: null,
			firstname: null,
			lastname: null,
			username: null,
			valid: false,
			passwordRejectionErrors: [],
			securityAckReuse: false,
			securityAckManager: false,
			url: "/knowledge",
			selectedCountry: null,
			countries: [
				{ country: "Afghanistan" }, { country: "Albania" }, { country: "Algeria" }, { country: "Andorra" }, { country: "Angola" }, { country: "Antigua and Barbuda" },
				{ country: "Argentina" }, { country: "Armenia" }, { country: "Australia" }, { country: "Austria" }, { country: "Azerbaijan" },
				{ country: "Bahamas" }, { country: "Bahrain" }, { country: "Bangladesh" }, { country: "Barbados" }, { country: "Belarus" }, { country: "Belgium" },
				{ country: "Belize" }, { country: "Benin" }, { country: "Bhutan" }, { country: "Bolivia" }, { country: "Bosnia and Herzegovina" }, { country: "Botswana" },
				{ country: "Brazil" }, { country: "Brunei" }, { country: "Bulgaria" }, { country: "Burkina Faso" }, { country: "Burundi" },
				{ country: "Cambodia" }, { country: "Cameroon" }, { country: "Canada" }, { country: "Cape Verde" }, { country: "Cayman Islands" }, { country: " Central African Republic" },
				{ country: "Chad" }, { country: "Chile" }, { country: "China" }, { country: "Christmas Island" }, { country: "Cocos (Keeling) Islands" }, { country: "Colombia" },
				{ country: "Comoros" }, { country: "Democratic Republic of the Congo" }, { country: "Congo, Republic of (Brazzaville)" }, { country: "  Cook Islands" },
				{ country: "Costa Rica" }, { country: "Côte D'ivoire (Ivory Coast)" }, { country: "Croatia" }, { country: "Cuba" }, { country: "Cyprus" }, { country: " Czech Republic" },
				{ country: "Denmark" }, { country: "Djibouti" }, { country: "Dominica" }, { country: "Dominican Republic" },
				{ country: "East Timor (Timor-Leste)" }, { country: "Ecuador" }, { country: "Egypt" }, { country: "El Salvador" }, { country: "Equatorial Guinea" }, { country: "Eritrea" },
				{ country: "Estonia" }, { country: "Ethiopia" },
				{ country: "Falkland Islands" }, { country: "Faroe Islands" }, { country: "Fiji" }, { country: "Finland" }, { country: "France" }, { country: "French Guiana" },
				{ country: "French Polynesia" }, { country: "French Southern Territories" },
				{ country: "Gabon" }, { country: "The Gambia" }, { country: "Georgia" }, { country: "Germany" }, { country: "Ghana" }, { country: "Gibraltar" },
				{ country: "Greece" }, { country: "Greenland" }, { country: "Grenada" }, { country: "Guadeloupe" }, { country: "Guam" }, { country: "Guatemala" },
				{ country: "Guinea" }, { country: "Guinea-Bissau" }, { country: "Guyana" },
				{ country: "Haiti" }, { country: "Holy See" }, { country: "Honduras" }, { country: "Hong Kong" }, { country: "Hungary" },
				{ country: "Iceland" }, { country: "India" }, { country: "Indonesia" }, { country: "Iran" }, { country: "Iraq" }, { country: "Ireland" },
				{ country: "Israel" }, { country: "Italy" }, { country: "Ivory Coast" },
				{ country: "Jamaica" }, { country: "Japan" }, { country: "Jordan" },
				{ country: "Kazakhstan" }, { country: "Kenya" }, { country: "Kiribati" }, { country: "Korea, Democratic People's Rep. (North Korea)" }, { country: "Korea, Republic of (South Korea)" },
				{ country: "Kosovo" }, { country: "Kuwait" }, { country: "Kyrgyzstan" },
				{ country: "Laos" }, { country: "Latvia" }, { country: "Lebanon" }, { country: "Lesotho" }, { country: "Liberia" }, { country: "Libya" },
				{ country: "Liechtenstein" }, { country: "Lithuania" }, { country: "Luxembourg" },
				{ country: "Macau" }, { country: "Madagascar" }, { country: "Malawi" }, { country: "Malaysia" }, { country: "Maldives" }, { country: "Mali" },
				{ country: "Malta" }, { country: "Marshall Islands" }, { country: "Martinique" }, { country: "Mauritania" }, { country: "Mauritius" }, { country: "Mayotte" },
				{ country: "Mexico" }, { country: "Micronesia" }, { country: "Moldova" }, { country: "Monaco" }, { country: "Mongolia" }, { country: "Montenegro" },
				{ country: "Montserrat" }, { country: "Morocco" }, { country: "Mozambique" }, { country: "Myanmar" },
				{ country: "Namibia" }, { country: "Nauru" }, { country: "Nepal" }, { country: "Netherlands" }, { country: "Netherlands Antilles" }, { country: "New Caledonia" },
				{ country: "New Zealand" }, { country: "Nicaragua" }, { country: "Niger" }, { country: "Nigeria" }, { country: "Niue" }, { country: "North Macedonia" },
				{ country: "Northern Mariana Islands" }, { country: "Norway" },
				{ country: "Oman" },
				{ country: "Pakistan" }, { country: "Palau" }, { country: "Palestine" }, { country: "Panama" }, { country: "Papua New Guinea" }, { country: "Paraguay" },
				{ country: "Peru" }, { country: "Philippines" }, { country: "Pitcairn Island" }, { country: "Poland" }, { country: "Portugal" }, { country: "Puerto Rico" },
				{ country: "Qatar" },
				{ country: "Reunion Island" }, { country: "Romania" }, { country: "Russian Federation" }, { country: "Rwanda" },
				{ country: "Saint Kitts and Nevis" }, { country: "Saint Lucia" }, { country: "Saint Vincent and the Grenadines" }, { country: "Samoa" },
				{ country: "San Marino" }, { country: "Sao Tome and Principe" }, { country: "Saudi Arabia" }, { country: "Senegal" }, { country: "Serbia" }, { country: "Seychelles" },
				{ country: "Sierra Leone" }, { country: "Singapore" }, { country: "Slovakia" }, { country: "Slovenia" }, { country: "Solomon Islands" }, { country: "Somalia" },
				{ country: "South Africa" }, { country: "South Sudan" }, { country: "Spain" }, { country: "Sri Lanka" }, { country: "Sudan" }, { country: "Suriname" },
				{ country: "Swaziland" }, { country: "Sweden" }, { country: "Switzerland" }, { country: "Syria" },
				{ country: "Taiwan" }, { country: "Tajikistan" }, { country: "Tanzania" }, { country: "Thailand" }, { country: "Tibet" }, { country: "Timor-Leste" },
				{ country: "Togo" }, { country: "Tokelau" }, { country: "Tonga" }, { country: "Trinidad and Tobago" }, { country: "Tunisia" }, { country: "Turkey" },
				{ country: "Turkmenistan" }, { country: "Turks and Caicos Islands" }, { country: "Tuvalu" },
				{ country: "Uganda" }, { country: "Ukraine" }, { country: "United Arab Emirates" }, { country: "United Kingdom" }, { country: "United States" },
				{ country: "Uruguay" }, { country: "Uzbekistan" },
				{ country: "Vanuatu" }, { country: "Vatican City State" }, { country: "Venezuela" }, { country: "Vietnam" },
				{ country: "Virgin Islands (British)" }, { country: "Virgin Islands (U.S.)" },
				{ country: "Wallis and Futuna Islands" }, { country: "Western Sahara" },
				{ country: "Yemen" },
				{ country: "Zambia" }, { country: "Zimbabwe" },
			],
			searchText: "",
			companyRegistrationNumber: "",
			countryCodes: [],
			// Search/autocomplete state
			apiClients: [],
			hasFetchedData: false,
			showCompanySuggestions: false,
			showEmailSuggestions: false,
			showPassword: false,
			showConfirmPassword: false,
			confirmGuest: false,
			isFetchingClients: false,
			fetchTimeout: null,
		}
	},
	mounted() {
		if (!this.invitationcode) {
			router.push("/invite")
		}
 
		mapState("connections/invitationcodes", {
			invitationcode: (state) => state.invitationcode,
		}),
		this.getclientname()
 
		document.addEventListener("mousedown", this.handleClickOutside)
	},
	beforeDestroy() {
		document.removeEventListener("mousedown", this.handleClickOutside)
	},
	methods: {
		//Fungerer ikke så lenge brukere må verifiseres
		//
		// login() {
		//  let username = this.username
		//  let password = this.password
		//  let loginpromise = store.dispatch("user/login", {
		//      username,
		//      password,
		//  })
		//  let that = this
		//  loginpromise
		//      .then(function () {
		//          router.push(that.$route.query.redirect || "/")
		//      })
		//      .catch((error) =>
		//          this.$store.dispatch(
		//              "loading/" + LoadingState.ACTION_SET_ERROR,
		//              error,
		//              { root: true }
		//          )
		//      )
		// },
		handleClickOutside(event) {
			const nameWrapper = this.$refs.nameWrapper
			if (nameWrapper && !nameWrapper.contains(event.target)) {
				this.showCompanySuggestions = false
			}
 
			const emailWrapper = this.$refs.emailWrapper
			if (emailWrapper && !emailWrapper.contains(event.target)) {
				this.showEmailSuggestions = false
			}
		},
		fetchClients() {
			if (this.hasFetchedData) return Promise.resolve()
			return ConnectionAPI.getExistingDetails()
				.then((data) => {
					this.apiClients = data
					this.hasFetchedData = true
				})
		},
		onNameInput() {
			if (this.name && this.name.length >= 1) {
				this.showCompanySuggestions = true
				this.fetchClients()
			} else {
				this.showCompanySuggestions = false
			}
		},
		onEmailInput() {
			if (this.email && this.email.length >= 1) {
				this.showEmailSuggestions = true
				this.fetchClients()
			} else {
				this.showEmailSuggestions = false
			}
		},
		continueAsGuest() {
			if (this.isDuplicateClient) return
			this.passwordRejectionErrors = []
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
 
			InvitationcodeAPI.createClient(this.invitationcode, this.name, this.selectedCountry, this.companyRegistrationNumber, this.password, this.email, this.firstname, this.lastname, 1)
				.then((result) => {
					this.username = result
					console.log(result)
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`A new user has been created! \n Your new username is '${result}', and your password is the one you set earlier.`,
						{root: true}
					)
					this.setInvitationCode({invitationcode:null})
					router.push("/verifyEmail")
					
				})
				.catch((error) => {
					const details = extractApiErrorDetails(error)
					if (details.length) {
						this.passwordRejectionErrors = details
					}
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		clearSearch() {
			this.searchText = ""
			this.filterCountries()
		},
 
 
		matchPasswordRule,
 
		selectClient(client) {
			this.name = client.ClientName
			this.email = client.Email
			this.showCompanySuggestions = false
			this.showEmailSuggestions = false
		},
		
		register() {
			if (this.isDuplicateClient) return
 
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
 
		
			InvitationcodeAPI.createClient(
				this.invitationcode,
				this.name,
				this.selectedCountry,
				this.companyRegistrationNumber,
				this.password,
				this.email,
				this.firstname,
				this.lastname,
				0
			)
 
				.then((result) => {
					this.username = result
			
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`A new user has been created! \n Your new username is '${result}', and your password is the one you set earlier.`,
						{ root: true }
					)
			
					this.setInvitationCode({ invitationcode: null })
					router.push("/verifyEmail")
				})
				.catch((error) => {
					const details = extractApiErrorDetails(error)
			
					if (details.length) {
						this.passwordRejectionErrors = details
					}
			
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		getclientname() {
			InvitationcodeAPI.getClientdata(this.invitationcode)
				.then((result) => {
					this.clientname = result[0]
					this.logoUrl = result[1]
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		...mapActions("connections/invitationcodes", ["setInvitationCode"]),
	},
	computed: {
		...mapState("connections/invitationcodes", {
			invitationcode: (state) => state.invitationcode,
		}),
		filteredCountries() {
			const searchTerm = this.searchText.toLowerCase().trim()
			if (!searchTerm) {
				return [...this.countries]
			} else {
				return this.countries.filter(
					(country) =>
						country.country.toLowerCase().includes(searchTerm)
				)
			}
		},
		filteredByCompany() {
			if (!this.name || this.name.length < 1) return []
			const query = this.name.toLowerCase()
			const matches = this.apiClients.filter(
				(client) =>
					client.ClientName &&
					client.ClientName.toLowerCase().includes(query)
			)

			const seen = new Set()
			return matches.filter((client) => {
				const key = client.ClientName.toLowerCase()
				if (seen.has(key)) return false
				seen.add(key)
				return true
			})
		},
 
		filteredByEmail() {
			if (!this.email || this.email.length < 1) return []
			const query = this.email.toLowerCase()
			return this.apiClients.filter(
				(client) =>
					client.Email &&
					client.Email.toLowerCase().includes(query)
			)
		},
		isDuplicateCompany() {
			if (!this.name) return false
			const nameQuery = this.name.trim().toLowerCase()
 
			return this.apiClients.some(
				(client) =>
					client.ClientName &&
					client.ClientName.trim().toLowerCase() === nameQuery
			)
		},
		isDuplicateEmail() {
			if (!this.email) return false
			const emailQuery = this.email.trim().toLowerCase()
 
			return this.apiClients.some(
				(client) =>
					client.Email &&
					client.Email.trim().toLowerCase() === emailQuery
			)
		},
		isDuplicateClient() {
			return this.isDuplicateCompany || this.isDuplicateEmail
		},
		duplicateError() {
			if (this.isDuplicateCompany && this.isDuplicateEmail) {
				return "This company already has an account, and this email cannot be used."
			}
			if (this.isDuplicateCompany) {
				return "This company already has an account."
			}
			if (this.isDuplicateEmail) {
				return "This email cannot be used."
			}
			return null
		},
	},
}
</script>
 
<style scoped>
.registration-card {
  border: 0.5px solid #E0E0E0;
  border-radius: 14px !important;
  overflow: hidden;
}
 
.card-header {
  background-color: #F7F7F5;
  border-bottom: 0.5px solid #E5E5E5;
}
 
.header-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}
 
.header-subtitle {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 0;
}
 
.reg-badge {
  background-color: #E6F7F3 !important;
  color: #0F6E56 !important;
  font-weight: 500;
  font-size: 15px;
}
 
/* Info banner */
/* .info-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background-color: #E6F7F3;
  border-left: 3px solid #0F6E56;
  border-radius: 0 8px 8px 0;
  padding: 10px 12px;
}
 
.info-icon {
  color: #0F6E56 !important;
  margin-top: 2px;
  flex-shrink: 0;
}
 
.info-text {
  font-size: 14px;
  color: #0F6E56;
  margin: 0;
  line-height: 1.6;
} */
 
/* Section label */
.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
 
/* Fields */
.styled-field {
  margin-bottom: 4px;
}
 
/* Hint text */
.hint-text {
  font-size: 14px;
  color: #999;
  margin-top: -8px;
}
 
.teal-link {
  color: #1e7c83;
  text-decoration: none;
}
 
.teal-link:hover {
  text-decoration: underline;
  cursor: pointer;
}
 
/* Register button */
.continue-btn {
  background-color: #1e7c83 !important;
  color: #fff !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  border-radius: 8px !important;
  height: 48px !important;
  text-transform: none !important;
}
 
/* Guest button */
/* .guest-btn {
  background-color: transparent !important;
  border: 1.5px solid #E19A3C !important;
  color: #E19A3C !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  text-transform: none !important;
  border-radius: 8px !important;
  height: 48px !important;
} */
 
.continue-btn:disabled {
  background-color: #D1D1CF !important;
  color: #999 !important;
}
 
/* .guest-btn:disabled {
  border-color: #D1D1CF !important;
  color: #999 !important;
  background-color: transparent !important;
} */
 
/* Login link */
.login-link-text {
  font-size: 14px;
  color: #666;
}
 
/* Help link */
.help-link {
  font-size: 14px;
  color: #999;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}
 
.help-link:hover {
  color: #555;
}
 
.confirm-card {
	border-radius: 16px;
	position: relative;
}
 
.dialog-icon-wrap {
	width: 56px;
	height: 56px;
	border-radius: 50%;
	background-color: #e3f1f1;
	display: flex;
	align-items: center;
	justify-content: center;
}
 
.dialog-title {
	font-size: 22px;
	font-weight: 700;
	color: #2b2b2b;
}
 
.dialog-subtitle {
	font-size: 14px;
	color: #6b6b6b;
	line-height: 1.6;
}
 
.guest-limits {
	list-style: none;
	padding: 0;
	margin: 0;
	text-align: left;
	display: inline-block;
}
 
.recommend-text {
	font-size: 15px;
	font-weight: 600;
	color: #0b7078;
	line-height: 1.4;
}
 
.register-btn {
	border-radius: 8px;
	text-transform: none;
	font-weight: 600;
}
 
.guest-link-btn {
	text-transform: none;
	font-weight: 500;
	text-decoration: underline;
}
 
.dialog-close-btn {
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 1;
}
 
.link-disabled {
	color: #ccc !important;
	cursor: not-allowed !important;
	pointer-events: none;
	text-decoration: none;
}
</style>
 